import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { Link, useHistory } from "react-router-dom";
import { FiPlus, FiUsers, FiTrash2, FiEdit3, FiX } from "react-icons/fi";
import Layout from "../../components/Layout/";
import Loading from "../../components/Loading";
import Select from "react-select";
import api from "../../services/api";
import refreshToken from "../../utils/refreshToken";
import { useAuth } from "../../hooks/auth";
import { useToast } from "../../hooks/toast";
import {
  TeamDetail,
  TeamListItem,
  TeamMember,
  unwrapTeamDetail,
  unwrapTeamsList,
} from "./teamApi";

interface IConsultant {
  id: number;
  first_name: string;
  email: string;
}

const inputShellClassName =
  "mt-2 rounded-2xl border border-neutral-400 bg-neutral-50 px-3 transition focus-within:border-[#dd6435] focus-within:bg-white focus-within:ring-2 focus-within:ring-[#dd6435]/15";

const inputClassName =
  "w-full appearance-none border-0 bg-transparent px-1 py-3 text-sm text-neutral-900 outline-none placeholder:text-neutral-400";

const TeamsAdmin: React.FC = () => {
  const history = useHistory();
  const { access_token, user, dateToExpires } = useAuth();
  const { addToast } = useToast();

  const [teams, setTeams] = useState<TeamListItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [createOpen, setCreateOpen] = useState(false);
  const [newTeamName, setNewTeamName] = useState("");

  const [manageTeam, setManageTeam] = useState<TeamDetail | null>(null);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [editName, setEditName] = useState("");
  const [consultants, setConsultants] = useState<IConsultant[]>([]);
  const [loadingConsultants, setLoadingConsultants] = useState(false);
  const [consultantToAddId, setConsultantToAddId] = useState<number | null>(null);

  const [deleteTarget, setDeleteTarget] = useState<TeamListItem | null>(null);

  const isAdmin = user?.role === "admin";

  const loadTeams = useCallback(async () => {
    const token = await refreshToken(dateToExpires, access_token);
    api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;
    const res = await api.get("/api/adm/teams");
    setTeams(unwrapTeamsList(res));
  }, [access_token, dateToExpires]);

  const loadConsultants = useCallback(async () => {
    const token = await refreshToken(dateToExpires, access_token);
    api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;
    const response = await api.get("/api/adm/users", {
      params: {
        role: "consultant",
      },
    });
    setConsultants(
      Array.isArray(response.data?.data?.consultants)
        ? response.data.data.consultants
        : []
    );
  }, [access_token, dateToExpires]);

  useEffect(() => {
    if (!user) {
      history.push("/");
      return;
    }
    if (!isAdmin) {
      history.push("/schedules");
      return;
    }
    (async () => {
      try {
        setLoading(true);
        setLoadingConsultants(true);
        await Promise.all([loadTeams(), loadConsultants()]);
      } catch (err: any) {
        addToast({
          title: err?.response?.data?.message ?? "Erro ao carregar times",
          type: "error",
        });
      } finally {
        setLoadingConsultants(false);
        setLoading(false);
      }
    })();
  }, [user, isAdmin, history, loadTeams, loadConsultants, addToast]);

  const openManage = useCallback(
    async (teamId: number) => {
      try {
        setLoadingDetail(true);
        const token = await refreshToken(dateToExpires, access_token);
        api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;
        const res = await api.get(`/api/adm/teams/${teamId}`);
        const detail = unwrapTeamDetail(res);
        if (detail) {
          setManageTeam(detail);
          setEditName(detail.name);
        }
      } catch (err: any) {
        addToast({
          title: err?.response?.data?.message ?? "Erro ao carregar time",
          type: "error",
        });
      } finally {
        setLoadingDetail(false);
      }
    },
    [access_token, dateToExpires, addToast]
  );

  const handleCreateTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newTeamName.trim()) return;
    setSaving(true);
    try {
      const token = await refreshToken(dateToExpires, access_token);
      api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;
      await api.post("/api/adm/teams", { name: newTeamName.trim() });
      addToast({ title: "Time criado", type: "success" });
      setCreateOpen(false);
      setNewTeamName("");
      await loadTeams();
    } catch (err: any) {
      addToast({
        title: err?.response?.data?.message ?? "Erro ao criar time",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleUpdateTeam = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!manageTeam || !editName.trim()) return;
    setSaving(true);
    try {
      const token = await refreshToken(dateToExpires, access_token);
      api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;
      await api.put(`/api/adm/teams/${manageTeam.id}`, {
        name: editName.trim(),
      });
      addToast({ title: "Time atualizado", type: "success" });
      setManageTeam({ ...manageTeam, name: editName.trim() });
      await loadTeams();
    } catch (err: any) {
      addToast({
        title: err?.response?.data?.message ?? "Erro ao atualizar",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteTeam = async () => {
    if (!deleteTarget) return;
    setSaving(true);
    try {
      const token = await refreshToken(dateToExpires, access_token);
      api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;
      await api.delete(`/api/adm/teams/${deleteTarget.id}`);
      addToast({ title: "Time removido", type: "success" });
      setDeleteTarget(null);
      if (manageTeam?.id === deleteTarget.id) setManageTeam(null);
      await loadTeams();
    } catch (err: any) {
      addToast({
        title: err?.response?.data?.message ?? "Erro ao remover time",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleRemoveMember = async (member: TeamMember) => {
    if (!manageTeam) return;
    setSaving(true);
    try {
      const token = await refreshToken(dateToExpires, access_token);
      api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;
      await api.delete(`/api/adm/teams/${manageTeam.id}/members/${member.id}`);
      addToast({ title: "Membro removido", type: "success" });
      await openManage(manageTeam.id);
      await loadTeams();
    } catch (err: any) {
      addToast({
        title: err?.response?.data?.message ?? "Erro ao remover membro",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const handleAddMember = async (userId: number) => {
    if (!manageTeam) return;
    setSaving(true);
    try {
      const token = await refreshToken(dateToExpires, access_token);
      api.defaults.headers.authorization = `Bearer ${token ?? access_token}`;
      await api.post(`/api/adm/teams/${manageTeam.id}/members`, {
        user_id: userId,
      });
      addToast({ title: "Membro adicionado", type: "success" });
      setConsultantToAddId(null);
      await openManage(manageTeam.id);
      await loadTeams();
    } catch (err: any) {
      addToast({
        title: err?.response?.data?.message ?? "Erro ao adicionar membro",
        type: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  const members = useMemo(() => manageTeam?.members ?? [], [manageTeam]);
  const consultantOptions = useMemo(() => {
    const currentMemberIds = new Set(members.map((m) => m.id));

    return consultants
      .filter((consultant) => !currentMemberIds.has(consultant.id))
      .map((consultant) => ({
        label: consultant.first_name,
        value: consultant.id,
      }));
  }, [consultants, members]);
  const menuPortalTarget =
    typeof document !== "undefined" ? document.body : null;

  if (!user || !isAdmin) return null;

  if (loading) {
    return (
      <div className="min-h-screen bg-neutral-50">
        <Layout>
          <div className="min-h-[calc(100vh-168px)]">
            <Loading />
          </div>
        </Layout>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-neutral-50 to-neutral-100">
        <Layout>
        <div className="min-h-[calc(100vh-168px)]">
        <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
          <div className="mb-8 flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-sm font-medium uppercase tracking-wide text-[#c4552b]">
                Administração
              </p>
              <h1 className="mt-1 text-3xl font-bold tracking-tight text-neutral-900">
                Times de consultores
              </h1>
              <p className="mt-2 max-w-xl text-sm text-neutral-600">
                Crie times, defina o nome e gerencie consultores e corretores
                parceiros. Os membros usam o time na agenda de visitas.
              </p>
            </div>
            <div className="flex gap-2">
              <Link
                to="/schedules"
                className="inline-flex items-center justify-center rounded-xl border border-neutral-200 bg-white px-4 py-2.5 text-sm font-medium text-neutral-700 shadow-sm transition hover:border-[#dd6435]/40 hover:text-[#c4552b]"
              >
                Voltar à agenda
              </Link>
              <button
                type="button"
                onClick={() => setCreateOpen(true)}
                className="inline-flex items-center gap-2 rounded-xl bg-[#dd6435] px-4 py-2.5 text-sm font-semibold text-white shadow-md shadow-orange-200/50 transition hover:bg-[#c4552b]"
              >
                <FiPlus className="h-4 w-4" />
                Novo time
              </button>
            </div>
          </div>

          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {teams.length === 0 && (
              <div className="col-span-full rounded-2xl border border-dashed border-neutral-300 bg-white/80 p-12 text-center">
                <FiUsers className="mx-auto h-12 w-12 text-neutral-300" />
                <p className="mt-4 text-neutral-600">
                  Nenhum time cadastrado. Clique em &quot;Novo time&quot; para
                  começar.
                </p>
              </div>
            )}
            {teams.map((t) => (
              <div
                key={t.id}
                className="group flex flex-col rounded-2xl border border-neutral-200/80 bg-white p-6 shadow-sm transition hover:border-[#dd6435]/30 hover:shadow-md"
              >
                <div className="flex items-start justify-between gap-2">
                  <div>
                    <h2 className="text-lg font-semibold text-neutral-900">
                      {t.name}
                    </h2>
                    {typeof t.members_count === "number" && (
                      <p className="mt-1 text-sm text-neutral-500">
                        {t.members_count}{" "}
                        {t.members_count === 1 ? "membro" : "membros"}
                      </p>
                    )}
                  </div>
                  <span className="rounded-full bg-[#fff5f0] px-2.5 py-0.5 text-xs font-medium text-[#c4552b]">
                    #{t.id}
                  </span>
                </div>
                <div className="mt-6 flex flex-wrap gap-2">
                  <button
                    type="button"
                    onClick={() => openManage(t.id)}
                    className="inline-flex flex-1 items-center justify-center gap-1.5 rounded-lg border border-neutral-200 bg-neutral-50 px-3 py-2 text-sm font-medium text-neutral-800 transition hover:border-[#dd6435]/40 hover:bg-white"
                  >
                    <FiEdit3 className="h-4 w-4" />
                    Gerenciar
                  </button>
                  <button
                    type="button"
                    onClick={() => setDeleteTarget(t)}
                    className="inline-flex items-center justify-center rounded-lg border border-red-100 bg-red-50 px-3 py-2 text-sm font-medium text-red-700 transition hover:bg-red-100"
                    aria-label={`Excluir ${t.name}`}
                  >
                    <FiTrash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {createOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
            <div className="w-full max-w-md rounded-2xl border border-neutral-200 bg-white p-6 shadow-2xl">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-neutral-900">
                  Novo time
                </h3>
                <button
                  type="button"
                  onClick={() => setCreateOpen(false)}
                  className="rounded-lg p-2 text-neutral-500 hover:bg-neutral-100"
                >
                  <FiX className="h-5 w-5" />
                </button>
              </div>
              <form onSubmit={handleCreateTeam} className="mt-4 space-y-4">
                <div>
                  <label className="block text-sm font-medium text-neutral-700">
                    Nome do time
                  </label>
                  <div className={inputShellClassName}>
                    <input
                      className={inputClassName}
                      value={newTeamName}
                      onChange={(e) => setNewTeamName(e.target.value)}
                      placeholder="Ex.: Time Fortaleza Sul"
                      autoFocus
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setCreateOpen(false)}
                    className="rounded-xl px-4 py-2 text-sm font-medium text-neutral-600 hover:bg-neutral-100"
                  >
                    Cancelar
                  </button>
                  <button
                    type="submit"
                    disabled={saving || !newTeamName.trim()}
                    className="rounded-xl bg-[#dd6435] px-4 py-2 text-sm font-semibold text-white hover:bg-[#c4552b] disabled:opacity-50"
                  >
                    {saving ? "Salvando…" : "Criar"}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {deleteTarget && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
            <div className="w-full max-w-md rounded-2xl border border-neutral-200 bg-white p-6 shadow-2xl">
              <h3 className="text-lg font-semibold text-neutral-900">
                Excluir time?
              </h3>
              <p className="mt-2 text-sm text-neutral-600">
                O time <strong>{deleteTarget.name}</strong> será removido. Esta
                ação pode afetar membros e a agenda vinculada ao time.
              </p>
              <div className="mt-6 flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setDeleteTarget(null)}
                  className="rounded-xl px-4 py-2 text-sm font-medium text-neutral-600 hover:bg-neutral-100"
                >
                  Cancelar
                </button>
                <button
                  type="button"
                  onClick={handleDeleteTeam}
                  disabled={saving}
                  className="rounded-xl bg-red-600 px-4 py-2 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50"
                >
                  {saving ? "Removendo…" : "Excluir"}
                </button>
              </div>
            </div>
          </div>
        )}

        {manageTeam && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
            <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-neutral-200 bg-white shadow-2xl">
              <div className="sticky top-0 flex items-center justify-between border-b border-neutral-100 bg-white px-6 py-4">
                <h3 className="text-lg font-semibold text-neutral-900">
                  Gerenciar time
                </h3>
                <button
                  type="button"
                  onClick={() => setManageTeam(null)}
                  className="rounded-lg p-2 text-neutral-500 hover:bg-neutral-100"
                >
                  <FiX className="h-5 w-5" />
                </button>
              </div>

              <div className="space-y-6 p-6">
                {loadingDetail ? (
                  <p className="text-sm text-neutral-500">Carregando…</p>
                ) : (
                  <>
                    <form onSubmit={handleUpdateTeam} className="space-y-3">
                      <label className="block text-sm font-medium text-neutral-700">
                        Nome
                      </label>
                      <div className="flex gap-2">
                        <div className={`flex-1 ${inputShellClassName} mt-0`}>
                          <input
                            className={inputClassName}
                            value={editName}
                            onChange={(e) => setEditName(e.target.value)}
                            placeholder="Nome do time"
                          />
                        </div>
                        <button
                          type="submit"
                          disabled={saving || !editName.trim()}
                          className="rounded-xl bg-neutral-900 px-4 py-2 text-sm font-medium text-white hover:bg-neutral-800 disabled:opacity-50"
                        >
                          Salvar
                        </button>
                      </div>
                    </form>

                    <div>
                      <h4 className="text-sm font-semibold text-neutral-800">
                        Membros ({members.length})
                      </h4>
                      <ul className="mt-3 divide-y divide-neutral-100 rounded-xl border border-neutral-100">
                        {members.length === 0 && (
                          <li className="px-4 py-6 text-center text-sm text-neutral-500">
                            Nenhum membro. Adicione abaixo.
                          </li>
                        )}
                        {members.map((m) => (
                          <li
                            key={m.id}
                            className="flex items-center justify-between gap-2 px-4 py-3"
                          >
                            <div>
                              <p className="text-sm font-medium text-neutral-900">
                                {m.first_name ?? "—"}
                              </p>
                              <p className="text-xs text-neutral-500">
                                {m.email ?? `ID ${m.id}`}
                              </p>
                            </div>
                            <button
                              type="button"
                              onClick={() => handleRemoveMember(m)}
                              disabled={saving}
                              className="rounded-lg px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                            >
                              Remover
                            </button>
                          </li>
                        ))}
                      </ul>
                    </div>

                    <div>
                      <h4 className="text-sm font-semibold text-neutral-800">
                        Adicionar membro
                      </h4>
                      <p className="mt-1 text-xs text-neutral-500">
                        Selecione um consultor da mesma lista usada no cadastro
                        de imóveis.
                      </p>
                      <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:items-start">
                        <div className="min-w-0 flex-1">
                          <Select
                            value={
                              consultantOptions.find(
                                (option) => option.value === consultantToAddId
                              ) ?? null
                            }
                            options={consultantOptions}
                            placeholder="Selecione um consultor"
                            noOptionsMessage={() =>
                              loadingConsultants
                                ? "Carregando"
                                : "Nenhum consultor disponível"
                            }
                            menuPlacement="auto"
                            menuPosition="fixed"
                            menuPortalTarget={menuPortalTarget ?? undefined}
                            onChange={(option) =>
                              setConsultantToAddId(option?.value ?? null)
                            }
                            styles={{
                              menuPortal: (base) => ({ ...base, zIndex: 70 }),
                            }}
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() =>
                            consultantToAddId != null &&
                            handleAddMember(consultantToAddId)
                          }
                          disabled={
                            saving ||
                            loadingConsultants ||
                            consultantToAddId == null
                          }
                          className="rounded-xl bg-[#dd6435] px-4 py-2.5 text-sm font-semibold text-white transition hover:bg-[#c4552b] disabled:cursor-not-allowed disabled:opacity-50"
                        >
                          Adicionar
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
        </div>
        </Layout>
    </div>
  );
};

export default TeamsAdmin;
